export interface BroadcastRecord {
  id: string;
  frequency: string;
  carrier_text: string;
  timestamp: number;
  stored_at: number;
}

type BroadcastHandler = (b: BroadcastRecord) => void;
type HistoryHandler = (broadcasts: BroadcastRecord[]) => void;
type StateHandler = (state: WSState) => void;

export type WSState = 'connecting' | 'open' | 'closed';

interface FlockWSOptions {
  url: string;
  onBroadcast?: BroadcastHandler;
  onHistory?: HistoryHandler;
  onState?: StateHandler;
}

const BACKOFF_STEPS = [1000, 2000, 4000, 8000, 15000, 30000] as const;

export class FlockWS {
  private url: string;
  private socket: WebSocket | null = null;
  private retryStep = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closedByUser = false;
  private onBroadcast?: BroadcastHandler;
  private onHistory?: HistoryHandler;
  private onState?: StateHandler;

  constructor(opts: FlockWSOptions) {
    this.url = opts.url;
    this.onBroadcast = opts.onBroadcast;
    this.onHistory = opts.onHistory;
    this.onState = opts.onState;
  }

  connect(): void {
    this.closedByUser = false;
    this.openSocket();
  }

  disconnect(): void {
    this.closedByUser = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket !== null) {
      this.socket.close();
      this.socket = null;
    }
    this.emitState('closed');
  }

  private openSocket(): void {
    this.emitState('connecting');
    const ws = new WebSocket(this.url);
    this.socket = ws;

    ws.addEventListener('open', () => {
      this.retryStep = 0;
      this.emitState('open');
    });

    ws.addEventListener('message', (event) => {
      let data: unknown;
      try {
        data = JSON.parse(typeof event.data === 'string' ? event.data : '');
      } catch {
        return;
      }
      const msg = data as { type?: string; data?: BroadcastRecord; broadcasts?: BroadcastRecord[] };
      if (msg.type === 'ping') {
        ws.send(JSON.stringify({ type: 'pong' }));
        return;
      }
      if (msg.type === 'broadcast' && msg.data !== undefined) {
        this.onBroadcast?.(msg.data);
        return;
      }
      if (msg.type === 'history' && Array.isArray(msg.broadcasts)) {
        this.onHistory?.(msg.broadcasts);
        return;
      }
    });

    ws.addEventListener('close', () => {
      this.socket = null;
      if (this.closedByUser) {
        this.emitState('closed');
        return;
      }
      const delay = BACKOFF_STEPS[Math.min(this.retryStep, BACKOFF_STEPS.length - 1)] ?? 30000;
      this.retryStep++;
      this.emitState('closed');
      this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
    });

    ws.addEventListener('error', () => {
      // close handler will run after error; rely on it for reconnect
    });
  }

  private emitState(state: WSState): void {
    this.onState?.(state);
  }
}
